import { useQuery } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { Alert, DataTable, Kpi, KpiGrid, Muted, PageHeader, Section } from '@/components/ui'
import {
  fetchAnalyticsAlertas,
  fetchAnalyticsDashboard,
  fetchScoresSetores,
} from './api'

export function AnalyticsPage() {
  const dash = useQuery({
    queryKey: ['analytics', 'dashboard'],
    queryFn: fetchAnalyticsDashboard,
  })
  const alertas = useQuery({
    queryKey: ['analytics', 'alertas'],
    queryFn: fetchAnalyticsAlertas,
  })
  const setores = useQuery({
    queryKey: ['analytics', 'scores', 'setores'],
    queryFn: fetchScoresSetores,
  })

  const t = dash.data?.turnover_mes
  const enps = dash.data?.enps

  return (
    <section className="hi-page">
      <PageHeader
        title="Analytics"
        subtitle="Indicadores organizacionais via API. Mapas avançados, DNA e simuladores permanecem no legado."
        actions={
          <a className="btn btn-secondary" href={legacyAppUrl('#/analytics')}>
            Analytics legado
          </a>
        }
      />

      {dash.isLoading ? <Muted>Carregando dashboard…</Muted> : null}
      {dash.isError ? (
        <Alert tone="danger">
          {dash.error instanceof ApiError ? dash.error.message : 'Falha ao carregar analytics.'}
        </Alert>
      ) : null}

      {dash.data ? (
        <KpiGrid>
          <Kpi label="Engajamento médio" value={fmtScore(dash.data.score_engajamento_medio)} />
          <Kpi label="Score geral médio" value={fmtScore(dash.data.score_geral_medio)} />
          <Kpi label="Alertas críticos" value={String(dash.data.alertas_criticos ?? 0)} />
          <Kpi label="Turnover do mês %" value={fmtScore(t?.taxa_turnover)} />
          <Kpi label="Admitidos" value={String(t?.admitidos ?? 0)} />
          <Kpi label="Demitidos" value={String(t?.demitidos ?? 0)} />
          <Kpi label="Headcount fim" value={String(t?.headcount_fim ?? 0)} />
          <Kpi label="eNPS" value={fmtScore(enps?.eNPS ?? null)} />
        </KpiGrid>
      ) : null}

      <Section
        title={`Alertas ativos${alertas.data?.nao_lidos ? ` · ${alertas.data.nao_lidos} não lidos` : ''}`}
      >
        {alertas.isLoading ? <Muted>Carregando…</Muted> : null}
        {(alertas.data?.dados || []).length === 0 && !alertas.isLoading ? (
          <Muted>Nenhum alerta ativo.</Muted>
        ) : (
          <ul className="hi-feed">
            {(alertas.data?.dados || []).slice(0, 12).map((a) => (
              <li key={a.id}>
                <strong>{a.titulo || a.tipo_display || a.tipo || 'Alerta'}</strong>
                <span>
                  {(a.prioridade || '-').toUpperCase()}
                  {a.mensagem || a.descricao ? ` · ${String(a.mensagem || a.descricao)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Scores por setor" flush>
        {(setores.data || []).length === 0 && !setores.isLoading ? (
          <div className="card-body">
            <Muted>Sem scores de setor neste período.</Muted>
          </div>
        ) : (
          <DataTable>
            <thead>
              <tr>
                <th>Departamento</th>
                <th>Score geral</th>
                <th>Engajamento</th>
              </tr>
            </thead>
            <tbody>
              {(setores.data || []).slice(0, 15).map((s, i) => (
                <tr key={String(s.id || i)}>
                  <td>{String(s.departamento_nome || s.departamento || '-')}</td>
                  <td>{fmtScore(s.score_geral)}</td>
                  <td>{fmtScore(s.score_engajamento)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Section>
    </section>
  )
}

function fmtScore(v: number | string | null | undefined) {
  if (v == null || v === '') return '-'
  const n = typeof v === 'number' ? v : Number(v)
  if (Number.isNaN(n)) return '-'
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })
}

export default AnalyticsPage
